import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Skeleton, SpecraButton, SpecraCard, SpecraCardContent, SpecraFormField, SpecraSelect, SpecraTextarea, specraButtonVariants } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { morgansClient, projectDataClient } from '../../../lib/connect/clients';
import { useLandscape, useLandscapeStory } from '../hooks/useLandscapes';
import { inferWhitespace } from '../whitespace';
export function LandscapeWhitespace({ landscapeId }) {
    const story = useLandscapeStory(landscapeId, false);
    const landscape = useLandscape(landscapeId);
    return /*#__PURE__*/ _jsxs("section", {
        "data-testid": Testids.landscapes.whitespacePanel,
        className: "space-y-3",
        "aria-label": "Whitespace analysis",
        children: [
            /*#__PURE__*/ _jsx("h2", {
                className: "text-base font-semibold",
                children: "Whitespace analysis"
            }),
            /*#__PURE__*/ _jsx("p", {
                className: "text-sm text-muted-foreground",
                children: "Inferred possibilities from permitted recorded evidence. A gap is not a recommendation or proof of no prior work."
            }),
            story.isLoading && /*#__PURE__*/ _jsx(Skeleton, {
                className: "h-24 w-full",
                "aria-label": "Loading permitted evidence"
            }),
            story.isError && /*#__PURE__*/ _jsxs("div", {
                role: "alert",
                children: [
                    "Could not load permitted evidence.",
                    ' ',
                    /*#__PURE__*/ _jsx(SpecraButton, {
                        onClick: ()=>story.refetch(),
                        children: "Retry"
                    })
                ]
            }),
            story.data && !story.isError && inferWhitespace(story.data).map((pattern)=>/*#__PURE__*/ _jsx(Pattern, {
                    landscapeId: landscapeId,
                    name: landscape.data?.name ?? 'Landscape',
                    intent: landscape.data?.scope ?? '',
                    keywords: landscape.data?.capabilities ?? [],
                    pattern: pattern
                }, pattern.key)),
            story.data && !story.isError && inferWhitespace(story.data).length === 0 && /*#__PURE__*/ _jsx("p", {
                children: "No supported pattern in the current permitted records. Missing or unavailable data does not prove no prior work."
            })
        ]
    });
}
function Pattern({ landscapeId, name, intent, keywords, pattern }) {
    const location = useLocation();
    const client = useQueryClient();
    const [investigating, setInvestigating] = useState(false);
    const [dismissing, setDismissing] = useState(false);
    const [reason, setReason] = useState('');
    const [profileId, setProfileId] = useState('');
    const key = [
        'landscape-pattern',
        landscapeId,
        pattern.key
    ];
    const state = useQuery({
        queryKey: key,
        queryFn: async ()=>(await projectDataClient.getLandscapePatternState({
                landscapeId,
                patternKey: pattern.key
            })).state
    });
    const profiles = useQuery({
        queryKey: [
            'tracking-profiles',
            'landscape'
        ],
        queryFn: async ()=>(await morgansClient.listTrackingProfiles({})).profiles
    });
    const dismiss = useMutation({
        mutationFn: ()=>projectDataClient.saveLandscapePatternState({
                landscapeId,
                state: {
                    patternKey: pattern.key,
                    dismissalReason: reason.trim(),
                    profileId: state.data?.profileId ?? ''
                }
            }),
        onSuccess: async ()=>{
            await client.invalidateQueries({
                queryKey: key
            });
            setDismissing(false);
        }
    });
    const sourceContext = `${name}: ${pattern.explanation}\n${pattern.caveat}\nSupporting permitted records: ${pattern.evidence.map((r)=>`${r.title} (${r.id})`).join('; ')}`;
    const proposal = {
        landscapeId,
        patternKey: pattern.key,
        returnTo: location.pathname + location.search,
        sourceContext,
        searchIntent: intent || 'Investigate recorded follow-through',
        keywords: keywords.length ? keywords : [
            name
        ]
    };
    if (state.isLoading) return /*#__PURE__*/ _jsx(Skeleton, {
        className: "h-24 w-full",
        "aria-label": "Loading recorded pattern decisions"
    });
    if (state.isError) return /*#__PURE__*/ _jsxs("div", {
        role: "alert",
        children: [
            "Could not load recorded decisions.",
            ' ',
            /*#__PURE__*/ _jsx(SpecraButton, {
                onClick: ()=>state.refetch(),
                children: "Retry"
            })
        ]
    });
    if (state.data?.dismissalReason) return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": Testids.landscapes.whitespaceDismissed,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            children: [
                /*#__PURE__*/ _jsxs("p", {
                    children: [
                        "Dismissed by you: ",
                        state.data.dismissalReason
                    ]
                }),
                /*#__PURE__*/ _jsx("p", {
                    children: pattern.caveat
                })
            ]
        })
    });
    return /*#__PURE__*/ _jsx(SpecraCard, {
        "data-testid": Testids.landscapes.whitespacePattern,
        "data-pattern-key": pattern.key,
        children: /*#__PURE__*/ _jsxs(SpecraCardContent, {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx("h3", {
                    className: "font-semibold",
                    children: pattern.title
                }),
                /*#__PURE__*/ _jsx("p", {
                    children: pattern.explanation
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "text-sm text-muted-foreground",
                    children: pattern.caveat
                }),
                /*#__PURE__*/ _jsx("ul", {
                    children: pattern.evidence.map((r)=>/*#__PURE__*/ _jsx("li", {
                            children: r.title || 'Untitled permitted Opportunity'
                        }, r.id))
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.landscapes.whitespaceInvestigate,
                    onClick: ()=>setInvestigating((v)=>!v),
                    children: "Investigate"
                }),
                investigating && /*#__PURE__*/ _jsxs("div", {
                    children: [
                        /*#__PURE__*/ _jsx("p", {
                            children: "Supporting permitted records; these associations do not grant additional access."
                        }),
                        pattern.evidence.map((r)=>/*#__PURE__*/ _jsxs("p", {
                                children: [
                                    r.title,
                                    " · Opportunity · ",
                                    r.id
                                ]
                            }, r.id))
                    ]
                }),
                profiles.isLoading && /*#__PURE__*/ _jsx("output", {
                    children: "Loading available Tracking Profiles…"
                }),
                profiles.isError && /*#__PURE__*/ _jsxs("div", {
                    role: "alert",
                    children: [
                        "Could not load Tracking Profiles.",
                        ' ',
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            onClick: ()=>profiles.refetch(),
                            children: "Retry"
                        })
                    ]
                }),
                profiles.data && /*#__PURE__*/ _jsx(SpecraFormField, {
                    label: "Create or refine a Tracking Profile",
                    children: /*#__PURE__*/ _jsxs(SpecraSelect, {
                        "data-testid": Testids.landscapes.whitespaceExistingProfile,
                        value: profileId,
                        onChange: (e)=>setProfileId(e.target.value),
                        children: [
                            /*#__PURE__*/ _jsx("option", {
                                value: "",
                                children: "Create a new Profile"
                            }),
                            profiles.data.map((p)=>/*#__PURE__*/ _jsxs("option", {
                                    value: p.id,
                                    children: [
                                        p.name,
                                        " · ",
                                        p.visibility
                                    ]
                                }, p.id))
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx(Link, {
                    "data-testid": Testids.landscapes.whitespaceProfile,
                    className: specraButtonVariants({
                        variant: 'outline',
                        size: 'sm'
                    }),
                    to: profileId ? `/opportunities/tracking-profiles/${encodeURIComponent(profileId)}/edit` : '/opportunities/tracking-profiles/create',
                    state: {
                        landscapeProposal: proposal
                    },
                    children: "Create/Refine Profile"
                }),
                state.data?.profileId && /*#__PURE__*/ _jsxs("p", {
                    children: [
                        "Linked Tracking Profile:",
                        ' ',
                        /*#__PURE__*/ _jsx(Link, {
                            to: `/opportunities/tracking-profiles/${encodeURIComponent(state.data.profileId)}`,
                            children: "View Profile"
                        })
                    ]
                }),
                /*#__PURE__*/ _jsx(SpecraButton, {
                    "data-testid": Testids.landscapes.whitespaceDismiss,
                    onClick: ()=>setDismissing(true),
                    children: "Dismiss pattern"
                }),
                dismissing && /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-2",
                    children: [
                        /*#__PURE__*/ _jsx(SpecraFormField, {
                            label: "Dismissal reason",
                            children: /*#__PURE__*/ _jsx(SpecraTextarea, {
                                "data-testid": Testids.landscapes.whitespaceDismissReason,
                                value: reason,
                                onChange: (e)=>setReason(e.target.value),
                                maxLength: 2000
                            })
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            "data-testid": Testids.landscapes.whitespaceDismissSave,
                            disabled: !reason.trim() || dismiss.isPending,
                            isLoading: dismiss.isPending,
                            onClick: ()=>dismiss.mutate(),
                            children: "Record dismissal"
                        }),
                        /*#__PURE__*/ _jsx(SpecraButton, {
                            disabled: dismiss.isPending,
                            onClick: ()=>setDismissing(false),
                            children: "Cancel"
                        }),
                        dismiss.isError && /*#__PURE__*/ _jsx("p", {
                            role: "alert",
                            children: "Could not record dismissal. Please retry."
                        })
                    ]
                })
            ]
        })
    });
}
